import { NgClass, NgStyle } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { ControlContainer, FormsModule, NgModelGroup, ReactiveFormsModule } from "@angular/forms";
import { ClassDirective, StyleDirective } from "@ngbracket/ngx-layout/extended";
import { FlexDirective, LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { ForgeCardModule, ForgeCheckboxProxyModule, ForgeIconButtonModule, ForgeIconModule, ForgeListItemModule, ForgeListModule, ForgeScaffoldModule, ForgeTextFieldModule, ForgeTooltipModule } from "@tylertech/forge-angular";
import { KeyValuePair } from "@tylertech/tyler-api-types";
import { Subject } from "rxjs";
import { debounceTime, takeUntil } from "rxjs/operators";
import { MetadataKey } from "../../../../interfaces";
import { MAX_RECORDS_RENDERED, MetadataInputSelectComponent } from "./metadata-input-select.component";
import { Utilities } from "@tylertech/shared-module";

@Component({
    selector: "metadata-input-dual-list",
    templateUrl: "./metadata-input-dual-list.tmpl.html",
    viewProviders: [{ provide: ControlContainer, useExisting: NgModelGroup }],
    imports: [
        ClassDirective,
        FlexDirective,
        LayoutAlignDirective,
        LayoutDirective,
        StyleDirective,
        ForgeCardModule,
        ForgeCheckboxProxyModule,
        ForgeIconButtonModule,
        ForgeIconModule,
        ForgeListItemModule,
        ForgeListModule,
        ForgeScaffoldModule,
        ForgeTextFieldModule,
        ForgeTooltipModule,
        FormsModule,
        NgClass,
        NgStyle,
        ReactiveFormsModule,
    ]
})
export class MetadataInputDualListComponent extends MetadataInputSelectComponent implements OnInit, OnDestroy
{
    private selectListSelections: MetadataKey[] = [];
    private sortUpdateSubject$ = new Subject<void>();
    private scrollHandler = (event: Event): void => this.infiniteScroll(event);

    get dualListSelectId(): string
    {
        return `${this.Name}DualSelectListCard`;
    }

    private deselectListSelections: MetadataKey[] = [];

    private _deselectList: KeyValuePair<MetadataKey, string>[] = [];
    get deselectList(): KeyValuePair<MetadataKey, string>[]
    {
        if (this._deselectList.length > 0)
            return this._deselectList;
        const selected: any = this.selected;
        if (!Utilities.IsNullOrUndefinedOrEmpty(selected) && !Utilities.IsNullOrUndefinedOrEmpty(this._options))
        {
            this._deselectList = this.applySort(this._options.filter(x => selected.indexOf(x.Key) > -1));
            return this._deselectList;
        }
        else
        {
            return [];

        }
    }

    override ngOnInit(): void
    {
        this.sortUpdateSubject$.pipe(
            debounceTime(1000),
            takeUntil(this.destructor$))
            .subscribe(() =>
            {
                return this.fireValueChanged(this.selected);
            });
        super.ngOnInit();
    }

    override ngOnDestroy(): void
    {
        const dualListSelectListCard = document.querySelector(`#${this.dualListSelectId}`);
        if (!Utilities.IsNullOrUndefined(dualListSelectListCard))
        {
            const cardBody = dualListSelectListCard.querySelector(".list-body");
            if (cardBody)
            {
                cardBody.removeEventListener("scroll", this.scrollHandler);
            }
        }
        this.sortUpdateSubject$.complete();
        super.ngOnDestroy();
    }

    onChange(): void
    {
        this.loadMoreCount = 1;
    }

    override selectItem(item: MetadataKey): void
    {
        if (this.isViewOnly)
            return;
        const index = this.selectListSelections.indexOf(item);
        if (index < 0)
            this.selectListSelections.push(item);
        else
            this.selectListSelections.splice(index, 1);
    }

    override setSelected(value: MetadataKey | MetadataKey[]): void
    {
        this._selected = this.applySort(this._options.filter(x => (<MetadataKey[]>value).indexOf(x.Key) > -1)).map(m => m.Key);
        this._deselectList = [];
    }

    override finalizeSelect(): void
    {
        const dualListSelectListCard = document.querySelector(`#${this.dualListSelectId}`)?.querySelector(".list-body");
        if (dualListSelectListCard)
        {
            dualListSelectListCard.addEventListener("scroll", this.scrollHandler);
        }
    }

    override fireValueChangedOnDeselect(): void
    {
        this.fireValueChanged(this.selected);
    }

    override lookupSuccess(): void
    {
        if (!this.propertyMetadata["$sorted"])
            this._options.forEach((x, i) => x["$sortedIndex"] = i);
    }

    override applySort(unsorted: KeyValuePair<MetadataKey, string>[]): KeyValuePair<MetadataKey, string>[]
    {
        return unsorted.sort((a, b) => a["$sortedIndex"] < b["$sortedIndex"] ? -1 : 1);
    }

    override getSelectList(): KeyValuePair<MetadataKey, string>[]
    {
        const selected: any = this.selected;
        if (selected)
        {
            if (this.optionSearch.length > 0)
            {
                return this._options
                    .filter(x => selected.indexOf(x.Key) === -1)
                    .filter(x => x.Value.toLowerCase()
                        .includes(this.optionSearch.toLowerCase()))
                    .slice(0, MAX_RECORDS_RENDERED * this.loadMoreCount);
            }
            else
            {
                return this._options
                    .filter(x => selected.indexOf(x.Key) === -1)
                    .slice(0, MAX_RECORDS_RENDERED * this.loadMoreCount);
            }
        }
        else
        {
            return this._options;
        }
    }

    override shouldLoadMore(): boolean
    {
        return this._options.filter(x => x.Value.toLowerCase()
            .includes(this.optionSearch.toLowerCase())).length - this.deselectList.length > MAX_RECORDS_RENDERED * this.loadMoreCount;
    }

    override addSelections(): void
    {
        const selected: any = this.selected;
        this.selected = this.applySort(this._options.filter(x => this.selectListSelections.indexOf(x.Key) > -1 && selected.indexOf(x.Key) === -1))
            .map(x => x.Key)
            .concat(this.selected);
        this.fireValueChanged(this.selected);
        this.selectListSelections = [];
    }

    override clearDeselectList(): void
    {
        this._deselectList = [];
    }

    removeSelections(): void
    {
        const selected: any = this.selected;
        this.selected = this.applySort(this._options.filter(x => this.deselectListSelections.indexOf(x.Key) === -1 && selected.indexOf(x.Key) > -1))
            .map(x => x.Key);
        this.fireValueChanged(this.selected);
        this.deselectListSelections = [];
    }

    deselectItem(item: MetadataKey): void
    {
        const index = this.deselectListSelections.indexOf(item);
        if (index < 0)
        {
            this.deselectListSelections.push(item);
        }
        else
        {
            this.deselectListSelections.splice(index, 1);
        }
    }

    sort(descending: boolean = false): void
    {
        const list: KeyValuePair<MetadataKey, string>[] = [];
        list.push(...this._options);
        list.sort((a, b) => this.compare(a, b, descending)).forEach((x, i) =>
        {
            this._options.find(o => o.Key === x.Key)["$sortedIndex"] = i;
        });
        this.updateAfterSort(false);
    }

    compare(a: KeyValuePair<MetadataKey, string>, b: KeyValuePair<MetadataKey, string>, descending: boolean): number
    {
        const aVal = a.Value.toLowerCase();
        const bVal = b.Value.toLowerCase();
        if (descending)
            return aVal > bVal ? -1 : 1;
        else
            return aVal < bVal ? -1 : 1;
    }

    moveUp(): void
    {
        const currentSelectedItem = this.deselectList.find(x => x.Key === this.deselectListSelections[0]);
        const currentSortedIndex = currentSelectedItem["$sortedIndex"];
        const indexOfAbove = this.deselectList.indexOf(currentSelectedItem) - 1;
        const aboveSortedIndex = this.deselectList[indexOfAbove]["$sortedIndex"];
        this._options.forEach(x =>
        {
            if (x["$sortedIndex"] >= aboveSortedIndex && x["$sortedIndex"] < currentSortedIndex)
                x["$sortedIndex"] = x["$sortedIndex"] + 1;
            else if (x["$sortedIndex"] === currentSortedIndex)
                x["$sortedIndex"] = aboveSortedIndex;
        });
        this.updateAfterSort(true);
    }

    moveDown(): void
    {
        const currentSelectedItem = this.deselectList.find(x => x.Key === this.deselectListSelections[0]);
        const currentSortedIndex = currentSelectedItem["$sortedIndex"];
        const indexOfBelow = this.deselectList.indexOf(currentSelectedItem) + 1;
        const belowSortedIndex = this.deselectList[indexOfBelow]["$sortedIndex"];
        this._options.forEach(x =>
        {
            if (x["$sortedIndex"] <= belowSortedIndex && x["$sortedIndex"] > currentSortedIndex)
                x["$sortedIndex"] = x["$sortedIndex"] - 1;
            else if (x["$sortedIndex"] === currentSortedIndex)
                x["$sortedIndex"] = belowSortedIndex;
        });
        this.updateAfterSort(true);
    }

    moveToTop(): void
    {
        const currentSelectedItem = this.deselectList.find(x => x.Key === this.deselectListSelections[0]);
        const currentSortedIndex = currentSelectedItem["$sortedIndex"];
        this._options.forEach(x =>
        {
            if (x["$sortedIndex"] < currentSortedIndex)
                x["$sortedIndex"] = x["$sortedIndex"] + 1;
            else if (x["$sortedIndex"] === currentSortedIndex)
                x["$sortedIndex"] = 0;
        });
        this.updateAfterSort(false);
    }

    moveToBottom(): void
    {
        const currentSelectedItem = this.deselectList.find(x => x.Key === this.deselectListSelections[0]);
        const currentSortedIndex = currentSelectedItem["$sortedIndex"];
        this._options.forEach(x =>
        {
            if (x["$sortedIndex"] > currentSortedIndex)
                x["$sortedIndex"] = x["$sortedIndex"] - 1;
            else if (x["$sortedIndex"] === currentSortedIndex)
                x["$sortedIndex"] = this._options.length - 1;
        });
        this.updateAfterSort(false);
    }

    private updateAfterSort(debounce: boolean): void
    {
        this.propertyMetadata["$sorted"] = true;
        this.selected = this._deselectList.map(x => x.Key);
        if (debounce)
            this.sortUpdateSubject$.next();
        else
            this.fireValueChanged(this.selected);
    }

    get moveUpDisabled(): boolean
    {
        return this.evaluateDisabled(0);
    }

    get moveDownDisabled(): boolean
    {
        return this.evaluateDisabled(this.deselectList.length - 1);
    }

    evaluateDisabled(index: number): boolean
    {
        if (!this.deselectList[0])
            return true;
        if (this.deselectListSelections.length !== 1)
            return true;
        if (this.deselectListSelections[0] === this.deselectList[index].Key)
            return true;
        return false;
    }
}
